import React, { useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { Divider, Grid2 as Grid, Typography, Alert, Stack, FormControl, OutlinedInput, InputAdornment, InputLabel, Select, MenuItem, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Search } from '@mui/icons-material';

import { useErrorBoundary } from 'react-error-boundary';
import YtdReport from '../../../types/reports/ytdReport';
import YearToDateReportsTable from './YearToDateReportsTable';
import ReportListExportSession from './ReportListExportSection';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const MayoralReports = (): JSX.Element => {
    
    document.title = 'OCPF - Mayoral Candidates Reports';

    const { showBoundary } = useErrorBoundary();  
    const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() - index);
    const [isLoading, setIsLoading] = useState(true);
    const [tableRows, setRows] = useState<YtdReport[]>();
    const [reports, setReports] = useState<YtdReport[]>();
    const [reportsSummary, setReportsSummary] = useState('No reports found.');
    const [reportYear, setReportYear] = useState(new Date().getMonth()===0?new Date().getFullYear()-1: new Date().getFullYear());
    const [showOnBallot, setShowOnBallot] = useState(false);
    
    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');

  useEffect(() => {
    getMayoralReportList();
  }, [reportYear, showOnBallot])

  const getMayoralReportList = async () => {

    setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/mayoral/depository/excelOutput/${reportYear}?onBallot=${showOnBallot}`);
    setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/mayoral/depository/textOutput/${reportYear}?onBallot=${showOnBallot}`);

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/mayoral/depository/${reportYear}?onBallot=${showOnBallot}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get mayoral reports.'));
          }
          return result.json();
        })
        .then(result => {
          let count = result.length;
          if (count === 0) {
            setReportsSummary("No reports found.");
            setRows([]);
            return;
          }
          else {
            setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
            setRows(result);
            setReports(result);
          }
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  const updateReporYear = (event: any) => {
    setReportYear(event.target.value);
  };

  const filterByName = debounce((event: any) => {
    let searchInput = event.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setRows(reports);
    }
    else {
      let filteredRows = reports?.filter((row) => row.filerName.toLowerCase().includes(searchInput)) || [];
      let count = filteredRows.length;
      setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
      setRows(filteredRows);
    }
  }, 800);

  const showOnBallotOnly = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnBallot(event.target.checked);
  };

  return (
    <Main>
      <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          marginBottom={2}
        >
          Mayoral Depository Year-to-Date Reports
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={{ xs: 12, md: 10 }}>
            <Stack direction={"row"} spacing={1}
              sx={{ mb: 1 }}
            >
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="year-id">Year</InputLabel>
                <Select
                  labelId="year-id"
                  id="year-select"
                  aria-labelledby='year-id'
                  sx={{ fontSize: 'smaller' }}
                  label="Report Year"
                  defaultValue={reportYear}
                  onChange={updateReporYear}
                >
                  {
                    dropDownYears.map((year) =>
                      <MenuItem value={year} key={year} >{year}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 200 }}>
                <OutlinedInput
                  aria-label='Search by filer name'
                  placeholder={"Filter by filer name"}
                  startAdornment={
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  }
                  onChange={filterByName}
                  sx={{ fontSize: 'smaller' }}
                />
              </FormControl>
              <FormControl size='small'>
                  <FormControlLabel control={<Checkbox checked={showOnBallot} onChange={showOnBallotOnly} inputProps={{ 'aria-label': 'show on ballot' }} />} label="Show on ballot only?" sx={{fontSize: "smaller"}} />
              </FormControl>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <ReportListExportSession excelExportUrl={excelExportUrl} txtExportUrl={txtExportUrl}></ReportListExportSession>
          </Grid>
          <Grid size={11.5}>
            <Alert icon={false} severity="info"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '.MuiAlert-message': {
                  padding: '4px',
                }
              }}
            >
              {reportsSummary}
            </Alert>
          </Grid>
        </Grid>
        {/*report table*/}
        <YearToDateReportsTable tableRows={tableRows} isCandidate={true}></YearToDateReportsTable>
        {/*loading indicator*/}
        <CircularProgress
          aria-label='Loading'
          size={50}
          className={isLoading ? "" : "hidden"}
          sx={{
            position: "fixed",
            left: "50%",
            top: "20%",
            transform: "translate(-50%, -50%)",
            zIndex: 2
          }}
        />
      </Container>
    </Main>
  );
};

export default MayoralReports;
